import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { RootReducer } from '@store/index';
import Calculator from '@components/Utilities/Calculator';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage, showAlertWarning } from '@utils/Alerts/Message';
import { showIOSAlert } from '@utils/Alerts/Confirm';
import { currencyFormatter } from '@utils/Numbers';
import { getCurrentPermissionSubject } from '@utils/Permissions';
import { toDateSafe } from '@utils/Date';
import { isActiveCashRegister } from '@utils/CashRegisters';
import {
    canMakeRecurring,
    defaultAmountMode,
    defaultPaidFromCash,
    describePurchaseConversion,
    describePurchasePrice,
    draftFromCashFlow,
    ExpenseDraft,
    formatPeriod,
    getCapturableCategories,
    getPayrollTeam,
    isMonthlyGroup,
    isNoteRequired,
    needsCashConfirmation,
    PAID_FROM_CASH_LABELS,
    purchaseHasBaseMode,
    purchaseUnitLabelOf,
    selectablePeriods,
    validateExpense,
    validateExpenseEdit
} from '@utils/Expenses';
import { CashFlow, ExpenseGroup, PurchaseUnitMode } from '@api/CashFlows/CashFlow';
import { Input } from '@api/Inputs/Input';
import ExpenseServ from '@api/CashFlows/ExpenseServ';
import { ExpenseCategory } from '@api/ExpenseCategories/ExpenseCategory';
import ExpenseCategoryServ from '@api/ExpenseCategories/ExpenseCategoryServ';
import BranchOfficeServ from '@api/BranchOffices/BranchOfficeServ';
import { getPosCatalog } from '@api/Catalog/CatalogCache';
import { AMOUNT_MODE_LABELS, RecurringAmountMode } from '@api/RecurringExpenses/RecurringExpense';
import ExpenseGroupChip from '@components/Expenses/ExpenseGroupChip';

interface Option {
    id: string;
    name: string;
}

const emptyDraft = (): ExpenseDraft => ({ amount: 0, note: '', paidFromCash: true });

type MonthlyChoice = RecurringAmountMode | 'once';

interface ExpenseFormProps {
    /** Sucursal del gasto. En edición, la del gasto. */
    branchOfficeId?: string;
    /** Editar este gasto (mismas reglas de categoría que capturar; §4.1 para el monto). */
    editing?: CashFlow;
    /** La caja del gasto ya cerró: el monto y "salió de la caja" no se tocan. */
    amountLocked?: boolean;
    /** Título arriba del teclado (el POS lo muestra; el diálogo del Informe ya tiene el suyo). */
    title?: string;
    onSaved?: (flow: CashFlow) => void;
}

/**
 * El formulario de un gasto (plan de Gastos §5.1, vistas 2–4). Lo usan el POS
 * (registro del día), la pestaña Gastos del Informe ("+ Registrar gasto", con
 * selector de sucursal) y la edición de un gasto.
 *
 * Lecturas: categorías ~25 docs la primera vez de la sesión y luego 0 (caché); el
 * equipo de la sucursal (~5–10 docs) solo al elegir Nómina; los insumos salen del
 * catálogo del POS (`catalog/snapshot`, 0–1 lecturas).
 */
const ExpenseForm: React.FunctionComponent<ExpenseFormProps> = ({ branchOfficeId, editing, amountLocked, title, onSaved }) => {
    const { branchOfficeSelected } = useSelector((state: RootReducer) => state.auth);
    const { cashRegister } = useSelector((state: RootReducer) => state.krispis);
    // Sacar dinero de la caja solo se puede en la sucursal en operación, con su caja abierta.
    const hasOpenCashRegister =
        !!branchOfficeId && branchOfficeId === branchOfficeSelected?.id && isActiveCashRegister(cashRegister, branchOfficeId);

    const [categories, setCategories] = useState<ExpenseCategory[]>([]);
    const [team, setTeam] = useState<Option[] | undefined>(undefined);
    // Los insumos COMPLETOS del catálogo: la compra necesita presentación, rendimiento y costo
    const [inputs, setInputs] = useState<Input[] | undefined>(undefined);
    const [draft, setDraft] = useState<ExpenseDraft>(emptyDraft);
    // El teclado guarda su propio importe: se remonta para volver a cero.
    const [calculatorKey, setCalculatorKey] = useState(0);
    const [monthlyOpen, setMonthlyOpen] = useState(false);
    const [monthlyChoice, setMonthlyChoice] = useState<MonthlyChoice>('once');
    const periods = useMemo(() => {
        const list = selectablePeriods(new Date());
        return editing?.period && !list.includes(editing.period) ? [editing.period, ...list] : list;
    }, [editing]);

    const category = draft.category;
    const subject = getCurrentPermissionSubject();
    const update = (changes: Partial<ExpenseDraft>) => setDraft((current) => ({ ...current, ...changes }));

    // La compra en la calle (plan de inventario F1): el insumo elegido, completo, para
    // convertir en vivo y comparar el precio contra el catálogo. Informa, no bloquea.
    const inputOptions = useMemo<Option[]>(() => (inputs || []).map((input) => ({ id: input.id!, name: input.name })), [inputs]);
    const selectedInput = useMemo(() => inputs?.find((input) => input.id === draft.input?.id), [inputs, draft.input?.id]);
    const purchaseMode: PurchaseUnitMode = draft.purchaseUnitMode === 'base' ? 'base' : 'presentation';
    const purchaseConversion = describePurchaseConversion(draft.purchaseQuantity || 0, purchaseMode, selectedInput);
    const purchasePrice = describePurchasePrice(draft.amount, draft.purchaseQuantity || 0, purchaseMode, selectedInput);
    const setPurchaseMode = (mode: PurchaseUnitMode) =>
        update({ purchaseUnitMode: mode, purchaseUnitLabel: purchaseUnitLabelOf(mode, selectedInput) });

    useEffect(() => {
        let alive = true;
        ExpenseCategoryServ.getExpenseCategories()
            .then((all) => {
                if (!alive) return;
                const capturable = getCapturableCategories(all, getCurrentPermissionSubject(), branchOfficeId);
                if (editing) {
                    const initial = draftFromCashFlow(editing, all);
                    setDraft(initial);
                    setCalculatorKey((key) => key + 1);
                    // La categoría actual se ofrece aunque ya no esté activa: el gasto la trae.
                    const current = initial.category;
                    setCategories(current && !capturable.some((c) => c.id === current.id) ? [current, ...capturable] : capturable);
                } else {
                    setCategories(capturable);
                }
            })
            .catch(showAlertError);
        return () => {
            alive = false;
        };
    }, [branchOfficeId, editing]);

    // Otra sucursal = otro equipo.
    useEffect(() => setTeam(undefined), [branchOfficeId]);

    useEffect(() => {
        if (!category?.askCollaborator || team || !branchOfficeId) return;
        BranchOfficeServ.getTeam(branchOfficeId)
            .then((users) => setTeam(getPayrollTeam(users, branchOfficeId).map((user) => ({ id: user.uid, name: user.name }))))
            .catch(showAlertError);
    }, [category, team, branchOfficeId]);

    useEffect(() => {
        if (!category?.askInput || inputs) return;
        getPosCatalog()
            .then((catalog) => setInputs(catalog.inputs.filter((input) => !!input.id)))
            .catch(showAlertError);
    }, [category, inputs]);

    // Un gasto de antes de F1 (o uno editado desde otra tablet) puede traer el insumo sin
    // la unidad de la compra: se completa del catálogo cuando llega.
    useEffect(() => {
        if (selectedInput && draft.input && !draft.purchaseUnitLabel) {
            update({ purchaseUnitLabel: purchaseUnitLabelOf(purchaseMode, selectedInput) });
        }
        // eslint-disable-next-line
    }, [selectedInput]);

    const onSelectCategory = (selected: ExpenseCategory | null) =>
        setDraft((current) => ({
            ...current,
            category: selected || undefined,
            // Al editar se respeta cómo se pagó; al capturar, lo que sugiere la categoría.
            paidFromCash: editing ? current.paidFromCash : defaultPaidFromCash(selected || undefined),
            payrollUser: undefined,
            input: undefined,
            purchaseQuantity: undefined,
            purchaseUnitMode: undefined,
            purchaseUnitLabel: undefined,
            period: selected && isMonthlyGroup(selected.group) ? current.period || periods[0] : undefined
        }));

    const reset = () => {
        setDraft(emptyDraft());
        setCalculatorKey((key) => key + 1);
    };

    // `toSave` explícito: "Fue con otro dinero" cambia el borrador y guarda en el mismo
    // clic, antes de que React comprometa el estado.
    const save = async (recurring?: RecurringAmountMode, toSave: ExpenseDraft = draft) => {
        showLoader();
        try {
            const saved = editing
                ? await ExpenseServ.updateExpense(editing, toSave)
                : await ExpenseServ.saveExpense(toSave, { branchOfficeId, recurring });
            if (saved.inventorySyncPending) {
                // El gasto SÍ quedó guardado; lo que falta es el inventario (el server lo
                // alcanza, o el aseo mañana). Si fue algo del gasto, se dice para corregirlo.
                showAlertWarning(
                    saved.inventorySyncError
                        ? `${editing ? 'Gasto actualizado' : 'Gasto registrado'}, pero la compra no entró al inventario: ${saved.inventorySyncError}. Corrígelo editando el gasto.`
                        : `${editing ? 'Gasto actualizado' : 'Gasto registrado'}; el inventario se actualiza en cuanto el servidor responda (a más tardar mañana con el aseo).`
                );
            } else {
                showAlertMessage(editing ? 'Gasto actualizado' : recurring ? 'Gasto fijo registrado: los próximos meses se generan solos' : 'Gasto registrado');
            }
            if (!editing) reset();
            onSaved?.(saved);
        } catch (error) {
            showAlertError(error);
        }
        hideLoader();
    };

    const problemOf = (): string | null =>
        editing
            ? validateExpenseEdit(editing, draft, { subject, amountLocked: !!amountLocked })
            : validateExpense(draft, { subject, branchOfficeId, hasOpenCashRegister });

    /** Después de las advertencias: el diálogo del mes o guardar. */
    const proceed = (toSave: ExpenseDraft) => {
        // Un gasto del mes (renta, luz…) se confirma: es fácil registrarlo dos veces. Y
        // quien captura fijos puede volverlo plantilla (vista 3).
        if (!editing && toSave.category && isMonthlyGroup(toSave.category.group) && toSave.period) {
            setMonthlyChoice(
                canMakeRecurring(toSave.category, subject, branchOfficeId) ? defaultAmountMode(toSave.category) : 'once'
            );
            setMonthlyOpen(true);
            return;
        }
        void save(undefined, toSave);
    };

    /**
     * Lo que conviene preguntar antes de guardar, todo en UN diálogo: que el dinero de
     * verdad salió de la caja de este turno (`needsCashConfirmation`) y que a esa
     * persona no se le pagó ya esta semana (`getRecentPayroll`). Pregunta, nunca
     * bloquea. Al editar solo se pregunta si el gasto PASA a salir de la caja.
     */
    const confirmAndProceed = async () => {
        const asksCash =
            needsCashConfirmation(draft) && (!editing || editing.paidFromCash === false);
        let repeated: CashFlow | undefined;
        if (!editing && category?.askCollaborator && draft.payrollUser?.uid) {
            showLoader();
            repeated = await ExpenseServ.getRecentPayroll(draft.payrollUser.uid);
            hideLoader();
        }
        if (!asksCash && !repeated) {
            proceed(draft);
            return;
        }
        const lines: string[] = [];
        if (repeated) {
            const when = toDateSafe(repeated.date);
            lines.push(
                `A ${draft.payrollUser?.name || 'esta persona'} ya se le registró nómina por ${currencyFormatter(
                    repeated.amount
                )}${when ? ` el ${format(when, "d 'de' MMMM", { locale: es })}` : ''}.`
            );
        }
        if (asksCash) {
            lines.push(
                `Lo vas a registrar como dinero que SALIÓ DE LA CAJA DE ESTE TURNO: el corte de hoy va a esperar ${currencyFormatter(
                    draft.amount
                )} menos.`,
                'Si se pagó con otro dinero (transferencia, efectivo guardado de otros días, dinero del dueño), elige "Fue con otro dinero" para que el corte no salga descuadrado.'
            );
        }
        showIOSAlert({
            title: repeated && !asksCash ? '¿Es otro pago?' : 'Antes de registrar',
            message: lines.join('\n\n'),
            buttons: [
                ...(asksCash
                    ? [
                          {
                              text: 'Fue con otro dinero',
                              onPress: () => {
                                  const changed = { ...draft, paidFromCash: false };
                                  setDraft(changed);
                                  proceed(changed);
                              }
                          },
                          { text: 'Sí, salió de la caja', onPress: () => proceed(draft) }
                      ]
                    : [{ text: 'Sí, registrar', onPress: () => proceed(draft) }]),
                { text: 'Revisar', style: 'cancel' as const, onPress: () => {} }
            ]
        });
    };

    const onSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        event.stopPropagation();
        const problem = problemOf();
        if (problem) {
            showAlertError(problem);
            return;
        }
        void confirmAndProceed();
    };

    const onConfirmMonthly = () => {
        setMonthlyOpen(false);
        void save(monthlyChoice === 'once' ? undefined : monthlyChoice);
    };

    const lockAmount = !!editing && !!amountLocked && editing.paidFromCash !== false;
    const canRecur = canMakeRecurring(category, subject, branchOfficeId);

    return (
        <form
            id={editing ? `edit-expense-form-${editing.id}` : 'add-expense-form'}
            onSubmit={onSubmit}
            style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
        >
            <Stack flex={1} minHeight={0} overflow="auto" p={{ xs: 2, md: 3 }} spacing={2}>
                <Stack direction="row" spacing={2} alignItems="center">
                    {title && (
                        <Typography variant="h6" fontWeight={600} flex={1}>
                            {title}
                        </Typography>
                    )}
                    <Stack
                        flex={1}
                        direction="row"
                        justifyContent="space-between"
                        border={1}
                        borderColor="divider"
                        borderRadius={4}
                        alignItems="center"
                        px={2}
                    >
                        <Typography variant="h6">$</Typography>
                        <Typography variant="h6" fontWeight={600}>
                            {draft.amount.toFixed(2)}
                        </Typography>
                    </Stack>
                </Stack>
                {lockAmount && (
                    <Alert severity="info">
                        La caja de este gasto ya cerró: el monto no se cambia. Si está mal, cancélalo y captúralo de nuevo.
                    </Alert>
                )}
                <Calculator
                    key={calculatorKey}
                    showAmount={false}
                    disabled={lockAmount}
                    initialValue={draft.amount}
                    onChangeValue={(amount) => update({ amount })}
                />

                <Autocomplete
                    options={categories}
                    groupBy={(option) => option.group}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={category || null}
                    onChange={(_, selected) => onSelectCategory(selected)}
                    renderGroup={(params) => (
                        <li key={params.key}>
                            <Box px={2} pt={1}>
                                <ExpenseGroupChip group={params.group as ExpenseGroup} />
                            </Box>
                            <ul style={{ padding: 0 }}>{params.children}</ul>
                        </li>
                    )}
                    renderInput={(params) => <TextField {...params} required label="Categoría" />}
                />

                {category?.askCollaborator && (
                    <Autocomplete
                        options={team || []}
                        loading={!team}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={draft.payrollUser ? { id: draft.payrollUser.uid, name: draft.payrollUser.name } : null}
                        onChange={(_, selected) =>
                            update({ payrollUser: selected ? { uid: selected.id, name: selected.name } : undefined })
                        }
                        noOptionsText="Nadie activo en esta sucursal. Primero hay que darlo de alta en Usuarios."
                        renderInput={(params) => <TextField {...params} required label="¿A quién se le paga?" />}
                    />
                )}

                {category?.askInput && (
                    <Autocomplete
                        options={inputOptions}
                        loading={!inputs}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={draft.input || null}
                        onChange={(_, selected) =>
                            update({
                                input: selected || undefined,
                                // Compran igual que al CEDIS (§8.1): arranca en presentaciones del catálogo
                                purchaseUnitMode: 'presentation',
                                purchaseUnitLabel: purchaseUnitLabelOf(
                                    'presentation',
                                    inputs?.find((input) => input.id === selected?.id)
                                )
                            })
                        }
                        noOptionsText='No está en la lista: usa "Otro insumo"'
                        renderInput={(params) => <TextField {...params} required label="Insumo" />}
                    />
                )}

                {/* La compra entra al inventario (plan de inventario F1): sin cantidad no
                    entra, así que es obligatoria. En presentaciones por default; la unidad
                    base queda como opción para una compra suelta. */}
                {category?.askInput && draft.input && (
                    <Stack spacing={0.5}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <TextField
                                required
                                type="number"
                                label="¿Cuánto compraste?"
                                inputProps={{ min: 0, step: 'any', inputMode: 'decimal' }}
                                value={draft.purchaseQuantity ?? ''}
                                onChange={(event) =>
                                    update({ purchaseQuantity: event.target.value === '' ? undefined : Number(event.target.value) })
                                }
                                sx={{ flex: 1 }}
                            />
                            {purchaseHasBaseMode(selectedInput) ? (
                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    color="primary"
                                    value={purchaseMode}
                                    onChange={(_, mode: PurchaseUnitMode | null) => mode && setPurchaseMode(mode)}
                                >
                                    <ToggleButton value="presentation">{purchaseUnitLabelOf('presentation', selectedInput) || 'presentación'}</ToggleButton>
                                    <ToggleButton value="base">{purchaseUnitLabelOf('base', selectedInput) || 'unidad base'}</ToggleButton>
                                </ToggleButtonGroup>
                            ) : (
                                <Typography variant="body2" color="text.secondary" minWidth={48}>
                                    {draft.purchaseUnitLabel || purchaseUnitLabelOf('presentation', selectedInput)}
                                </Typography>
                            )}
                        </Stack>
                        {(purchaseConversion || purchasePrice) && (
                            <Typography variant="caption" color="text.secondary">
                                {[purchaseConversion, purchasePrice].filter(Boolean).join(' · ')}
                            </Typography>
                        )}
                    </Stack>
                )}

                {category && isMonthlyGroup(category.group) && (
                    <FormControl fullWidth>
                        <InputLabel id="expense-period-label">¿De qué mes es?</InputLabel>
                        <Select
                            labelId="expense-period-label"
                            label="¿De qué mes es?"
                            value={draft.period || ''}
                            onChange={(event) => update({ period: event.target.value })}
                        >
                            {periods.map((period) => (
                                <MenuItem key={period} value={period}>
                                    {formatPeriod(period)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                <TextField
                    required={isNoteRequired(draft)}
                    fullWidth
                    multiline
                    rows={3}
                    label={isNoteRequired(draft) ? 'Descripción' : 'Nota (opcional)'}
                    value={draft.note}
                    onChange={(event) => update({ note: event.target.value })}
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={draft.paidFromCash}
                            disabled={lockAmount}
                            onChange={(event) => update({ paidFromCash: event.target.checked })}
                        />
                    }
                    label={draft.paidFromCash ? PAID_FROM_CASH_LABELS.fromCash : PAID_FROM_CASH_LABELS.otherMoney}
                />
                <Typography variant="caption" color="text.secondary" mt="-8px !important">
                    {PAID_FROM_CASH_LABELS.hint}
                </Typography>
                {!editing && draft.paidFromCash && !hasOpenCashRegister && (
                    <Typography variant="body2" color="error">
                        {branchOfficeId && branchOfficeId !== branchOfficeSelected?.id
                            ? 'Desde aquí no se sabe si esa sucursal tiene caja abierta: solo se registra lo pagado con otro dinero.'
                            : 'No hay caja abierta: solo se puede registrar un gasto pagado con otro dinero.'}
                    </Typography>
                )}

                <Stack direction="row" justifyContent="space-between">
                    <Typography variant="h5" fontWeight={600}>
                        Total
                    </Typography>
                    <Typography variant="h5" fontWeight={600}>
                        {currencyFormatter(draft.amount)}
                    </Typography>
                </Stack>
                <Button variant="contained" color="success" type="submit">
                    {editing ? 'Guardar cambios' : 'Registrar gasto'}
                </Button>
            </Stack>

            <Dialog open={monthlyOpen} onClose={() => setMonthlyOpen(false)} fullWidth maxWidth="xs" PaperProps={{ style: { borderRadius: 15 } }}>
                <DialogTitle fontWeight={600}>
                    {category ? `${category.name} de ${draft.period ? formatPeriod(draft.period) : ''}` : ''}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <Typography>
                            {`Estás por registrar ${category?.name || 'un gasto'} por ${currencyFormatter(draft.amount)}, ${
                                draft.paidFromCash ? 'que salió de la caja de este turno' : 'pagado con otro dinero'
                            }.`}
                        </Typography>
                        {canRecur && (
                            <FormControl fullWidth>
                                <InputLabel id="monthly-choice-label">¿Se repite cada mes?</InputLabel>
                                <Select
                                    labelId="monthly-choice-label"
                                    label="¿Se repite cada mes?"
                                    value={monthlyChoice}
                                    onChange={(event) => setMonthlyChoice(event.target.value as MonthlyChoice)}
                                >
                                    <MenuItem value="fixed">{`Sí — ${AMOUNT_MODE_LABELS.fixed.toLowerCase()}`}</MenuItem>
                                    <MenuItem value="variable">{`Sí — ${AMOUNT_MODE_LABELS.variable.toLowerCase()}`}</MenuItem>
                                    <MenuItem value="once">No, solo esta vez</MenuItem>
                                </Select>
                            </FormControl>
                        )}
                        {canRecur && monthlyChoice !== 'once' && (
                            <Alert severity="info">
                                {monthlyChoice === 'fixed'
                                    ? 'Cada mes se registra solo, con este monto.'
                                    : 'Cada mes se registra solo con el último monto, marcado "por confirmar", y el admin de la sucursal escribe el del recibo.'}
                            </Alert>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMonthlyOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={onConfirmMonthly}>
                        Registrar
                    </Button>
                </DialogActions>
            </Dialog>
        </form>
    );
};

export default ExpenseForm;
